/** @format */

import { parseMathSegments, parseMarkdownRuns } from "@question-bank/shared";
import katex from "katex";
import "katex/dist/katex.min.css";
import { Fragment, useMemo } from "react";
import renderA11yString from "katex/contrib/render-a11y-string";

interface MathTextProps {
	/** Content-block text that may embed LaTeX as `$...$` / `$$...$$`. and
	 * markdwon emphasis (`**bold**` / `*italic*` / `***both***`). */
	text: string;
}

type RenderedSegment =
	| { type: "text"; value: string }
	| { type: "math"; html: string; label: string; display: boolean };
const renderProse = (value: string) =>
	parseMarkdownRuns(value).map((run, index) => {
		if (run.isBold && run.isItalic) {
			return (
				<strong key={index}>
					<em>{run.value}</em>
				</strong>
			);
		}
		if (run.isBold) {
			return <strong key={index}>{run.value}</strong>;
		}
		if (run.isItalic) {
			return <em key={index}>{run.value}</em>;
		}
		return <Fragment key={index}>{run.value}</Fragment>;
	});

const describeMath = (tex: string): string => {
	// TODO: KaTeX has no welsh, so welsh users hear english descriptions
	try {
		return renderA11yString(tex);
	} catch (error) {
		console.warn(`MathText: could not describe formula "${tex}" for screen readers`, error);
		return tex;
	}
};

/**
 * Render a content string, turning any embedded LaTeX runs into typeset maths
 * via KaTeX while markdown emphasis in the surrounding prose become `<strong>` / `<em>` elements.
 *  Markdown is parsed after the maths split, so asterisks inside `$...$` never count as
 * emphasis. KaTeX runs with `trust: false` (the default) so author LaTeX cannot inject markup, and `throwOnError:
 * false` renders a malformed formula in red rather than crashing the view.
 */

export const MathText = ({ text }: MathTextProps) => {
	const segments = useMemo(
		() =>
			parseMathSegments(text).map((segment): RenderedSegment => {
				if (segment.type === "text") {
					return { type: "text", value: segment.value };
				}
				return {
					type: "math",
					html: katex.renderToString(segment.value, {
						displayMode: segment.display,
						throwOnError: false,
					}),
					label: describeMath(segment.value),
					display: segment.display,
				};
			}),
		[text],
	);

	return (
		<>
			{segments.map((segment, index) => {
				if (segment.type === "text") {
					return <Fragment key={index}>{renderProse(segment.value)}</Fragment>;
				}

				return (
					<span
						key={index}
						className={segment.display ? "math-display" : "math-inline"}
						role="math"
						aria-label={segment.label}
						// KaTeX output is trusted, sanitised HTML derived from the LaTeX
						// source (raw-HTML commands are disabled via `trust: false`).
						dangerouslySetInnerHTML={{ __html: segment.html }}
					/>
				);
			})}
		</>
	);
};
